import { CircularProgress, TextField, TextFieldProps } from '@mui/material';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { useDebounce } from '@hooks/useDebounce';

export type DebounceTextFieldHandle = {
  resetDebouncedTextFieldValue: () => void;
  setValue: (data: string) => void;
};

type Props = TextFieldProps & {
  defaultValue?: string | null;
  onChangeDebounced: (data: string) => void;
  isLoading?: boolean;
  onDebounceChange?: (isDebounsing: boolean) => void;
};

const DebounceTextField = forwardRef<DebounceTextFieldHandle, Props>(
  ({ onChangeDebounced, defaultValue, isLoading, onDebounceChange, ...textFieldProps }: Props, ref) => {
    const debounceStatus = useRef(false);
    const [value, setValue] = useState<string>(defaultValue || '');
    const debounceValue = useDebounce(value);

    const debounceRef = useRef(debounceValue);

    const isDebouncing = debounceValue !== value;

    useEffect(() => {
      if (debounceStatus.current !== isDebouncing) {
        debounceStatus.current = isDebouncing;
        if (onDebounceChange) {
          onDebounceChange(isDebouncing);
        }
      }
    }, [isDebouncing, onDebounceChange]);

    useImperativeHandle(
      ref,
      () => {
        return {
          resetDebouncedTextFieldValue: () => setValue(''),
          setValue,
        };
      },
      [],
    );

    if (debounceValue !== debounceRef.current) {
      onChangeDebounced(debounceValue);
      debounceRef.current = debounceValue;
    }

    return (
      <TextField
        fullWidth
        value={value}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setValue(event.target.value)}
        slotProps={{
          input: {
            ...textFieldProps?.InputProps,
            endAdornment: (
              <>
                {debounceValue !== value || isLoading ? (
                  <CircularProgress aria-label="Loading..." color="inherit" size={20} />
                ) : null}
                {textFieldProps?.InputProps?.endAdornment}
              </>
            ),
          },
        }}
        {...textFieldProps}
      />
    );
  },
);

export default DebounceTextField;
